import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AuthImage from "@/components/AuthImage";
import { Loader2, Sparkles, ArrowUpRight, Crown } from "lucide-react";
import { toast } from "sonner";

export default function AnalysisDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tryOnLoadingId, setTryOnLoadingId] = useState(null);
  const [tryOns, setTryOns] = useState({});

  useEffect(() => {
    api.get(`/analyses/${id}`)
      .then((r) => setAnalysis(r.data))
      .catch(() => toast.error("Could not load analysis"))
      .finally(() => setLoading(false));
  }, [id]);

  const generateTryOn = async (styleId) => {
    if (!user?.is_premium) {
      toast.error("Virtual try-on is a Premium feature.");
      return;
    }
    setTryOnLoadingId(styleId);
    try {
      const res = await api.post("/tryon", { analysis_id: id, style_id: styleId });
      setTryOns((prev) => ({ ...prev, [styleId]: res.data.result_path }));
      toast.success("Try-on ready.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Try-on failed");
    } finally {
      setTryOnLoadingId(null);
    }
  };

  if (loading) {
    return (
      <main className="crown-container py-20 text-center" data-testid="analysis-loading">
        <Loader2 className="animate-spin text-gold mx-auto" strokeWidth={1.5} />
        <p className="font-overline mt-4">Loading your reading…</p>
      </main>
    );
  }
  if (!analysis) {
    return <main className="crown-container py-20" data-testid="analysis-empty">Analysis not found.</main>;
  }

  const r = analysis.result;

  return (
    <main className="crown-container py-12 md:py-16" data-testid="analysis-detail-page">
      <p className="font-overline mb-3">Reading №{analysis.id.slice(-6)}</p>
      <h1 className="font-serif text-4xl sm:text-5xl leading-tight mb-2">Your editorial profile.</h1>
      <p className="text-ash mb-12 max-w-2xl">{r.style_notes}</p>

      {/* Profile facets */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-px bg-bone/10 mb-16">
        {[
          ["Face shape", r.face_shape],
          ["Hair type", r.hair_type],
          ["Density", r.hair_density],
          ["Skin tone", r.skin_tone],
        ].map(([k, v]) => (
          <div key={k} className="bg-espresso p-6 md:p-8" data-testid={`facet-${k.toLowerCase().replace(' ', '-')}`}>
            <p className="font-overline text-ash">{k}</p>
            <p className="font-serif text-3xl mt-3 capitalize text-gold">{v}</p>
          </div>
        ))}
      </section>

      {/* Recommendations */}
      <section data-testid="recommendations-section">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="font-overline flex items-center gap-2"><Sparkles size={12} strokeWidth={1.5} /> Curated for you</p>
            <h2 className="font-serif text-3xl mt-2">Eight styles, your shape.</h2>
          </div>
          {!user?.is_premium && (
            <Link to="/pricing" className="font-overline text-gold flex items-center gap-1" data-testid="premium-cta-tryon">
              <Crown size={14} strokeWidth={1.5} /> Unlock virtual try-on
            </Link>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {analysis.recommended_styles.map((s) => (
            <article key={s.id} className="crown-card group" data-testid={`rec-card-${s.id}`}>
              <div className="aspect-[16/10] overflow-hidden relative">
                {tryOns[s.id] ? (
                  <AuthImage path={tryOns[s.id]} alt={`${s.name} try-on`} className="w-full h-full object-cover" testId={`tryon-image-${s.id}`} />
                ) : (
                  <img src={s.image_url} alt={s.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                )}
                <div className="absolute top-3 left-3 font-overline text-[0.65rem] bg-espresso/80 backdrop-blur px-2 py-1 text-gold">
                  {s.category}
                </div>
                {tryOns[s.id] && (
                  <div className="absolute top-3 right-3 font-overline text-[0.65rem] bg-gold text-espresso px-2 py-1">
                    Try-on
                  </div>
                )}
              </div>
              <div className="p-6">
                <h3 className="font-serif text-2xl mb-2">{s.name}</h3>
                <p className="text-ash text-sm leading-relaxed line-clamp-2 mb-5">{s.description}</p>
                <div className="flex flex-wrap gap-2">
                  <Link to={`/style/${s.id}`} className="crown-btn-ghost text-[0.7rem] py-2 px-4" data-testid={`view-style-${s.id}`}>
                    View details
                  </Link>
                  <button
                    onClick={() => generateTryOn(s.id)}
                    disabled={tryOnLoadingId === s.id || !user?.is_premium}
                    className="crown-btn-primary text-[0.7rem] py-2 px-4 disabled:opacity-40"
                    data-testid={`tryon-btn-${s.id}`}
                  >
                    {tryOnLoadingId === s.id ? (
                      <><Loader2 className="animate-spin" size={12} strokeWidth={1.5} /> Generating</>
                    ) : (
                      <>Virtual try-on <ArrowUpRight size={12} strokeWidth={1.5} /></>
                    )}
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}
